// ============================================================
// Auth Store (Zustand)
// ============================================================

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface User {
    id: string;
    email: string;
    name: string;
    role?: string;
    avatarUrl?: string;
}

interface Tenant {
    id: string;
    name: string;
    slug: string;
    settings?: Record<string, unknown>;
}

interface AuthState {
    user: User | null;
    tenant: Tenant | null;
    accessToken: string | null;
    refreshToken: string | null;
    isAuthenticated: boolean;

    setAuth: (data: { user: User; tenant: Tenant; accessToken: string; refreshToken: string; role?: string }) => void;
    logout: () => void;
    refreshAccessToken: () => Promise<boolean>;
}

export const useAuthStore = create<AuthState>()(
    persist(
        (set, get) => ({
            user: null,
            tenant: null,
            accessToken: null,
            refreshToken: null,
            isAuthenticated: false,

            setAuth: (data) => {
                set({
                    user: { ...data.user, role: data.role || data.user.role },
                    tenant: data.tenant,
                    accessToken: data.accessToken,
                    refreshToken: data.refreshToken,
                    isAuthenticated: true,
                });
            },

            logout: () => {
                // Sign out of Firebase too
                import('../lib/firebase').then(({ firebaseSignOut }) => firebaseSignOut()).catch(() => { });
                set({
                    user: null,
                    tenant: null,
                    accessToken: null,
                    refreshToken: null,
                    isAuthenticated: false,
                });
            },

            refreshAccessToken: async () => {
                const { refreshToken } = get();
                if (!refreshToken) return false;

                try {
                    const apiBase = import.meta.env.VITE_API_URL || '/api';
                    const res = await fetch(`${apiBase}/auth/refresh`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ refreshToken }),
                    });

                    if (!res.ok) return false;

                    const data = await res.json();
                    set({
                        accessToken: data.data.accessToken,
                        refreshToken: data.data.refreshToken,
                    });
                    return true;
                } catch {
                    return false;
                }
            },
        }),
        {
            name: 'hirehub-auth',
            partialize: (state) => ({
                user: state.user,
                tenant: state.tenant,
                accessToken: state.accessToken,
                refreshToken: state.refreshToken,
                isAuthenticated: state.isAuthenticated,
            }),
        }
    )
);
